import {
  ESCALA_DA_POSICAO,
  type GravidadeDeAvaria,
  ROTULO_DA_GRAVIDADE,
  ROTULO_DO_TIPO_DE_AVARIA,
  type TipoDeAvaria,
} from '@locaveri/shared'
import type { KeyboardEvent, MouseEvent, ReactElement } from 'react'

import { cn } from '@/lib/utils'

/**
 * O diagrama do carro (vista superior) onde as avarias são marcadas.
 *
 * ---------------------------------------------------------------------------
 * A POSIÇÃO É EM MILÉSIMOS DO VIEWBOX, E O VIEWBOX É 0..1000
 * ---------------------------------------------------------------------------
 * O contrato do domínio (`vistoria.ts` do shared) guarda a avaria em MILÉSIMOS
 * inteiros da largura e da altura — nunca em pixels — para o mesmo ponto cair
 * no mesmo lugar no painel de 27" e no celular do pátio. O viewBox é
 * `0 0 1000 1000` DE PROPÓSITO: milésimo e unidade do desenho coincidem, e a
 * conversão clique → posição é uma regra de três sobre o `getBoundingClientRect`
 * sem nenhum fator mágico no meio.
 *
 * ---------------------------------------------------------------------------
 * TEMA-NEUTRO POR `currentColor`
 * ---------------------------------------------------------------------------
 * O carro é desenhado com `stroke="currentColor"` e preenchimentos com opacidade
 * baixa: quem escolhe a cor é a classe de texto do container, e o mesmo SVG
 * funciona no tema escuro e no claro sem uma segunda versão. Os marcadores de
 * avaria usam os tokens SEMÂNTICOS (`--info`/`--warning`/`--danger`) — nunca o
 * amarelo da marca, que aqui seria lido como aviso (regra do CLAUDE.md). O
 * número é preto porque os três hexes semânticos são vivos o bastante para
 * passar contraste sobre preto nos dois temas.
 *
 * ---------------------------------------------------------------------------
 * TECLADO MARCA O CENTRO
 * ---------------------------------------------------------------------------
 * Apontar uma posição livre é gesto de ponteiro. Para não deixar quem navega
 * por teclado sem caminho nenhum, Enter/Espaço marcam o CENTRO do diagrama —
 * a posição exata pode ser dita na descrição da avaria, que é obrigatória.
 */

/** O mínimo que o diagrama precisa saber de uma avaria — local ou vinda da API. */
export interface AvariaNoDiagrama {
  readonly tipo: TipoDeAvaria
  readonly gravidade: GravidadeDeAvaria
  readonly posicaoXEmMilesimos: number
  readonly posicaoYEmMilesimos: number
  readonly descricao?: string
}

/**
 * Gravidade → variante de `Badge`. Exportado para as listas de avaria da tela
 * usarem a MESMA cor do marcador — cor divergindo entre diagrama e lista faria
 * o "3" vermelho parecer outro item do "3" da lista.
 */
export const BADGE_DA_GRAVIDADE: Readonly<
  Record<GravidadeDeAvaria, 'info' | 'warning' | 'danger'>
> = {
  LEVE: 'info',
  MODERADA: 'warning',
  GRAVE: 'danger',
}

/** A cor de preenchimento do marcador, pelos tokens semânticos do tema. */
const PREENCHIMENTO_DA_GRAVIDADE: Readonly<Record<GravidadeDeAvaria, string>> = {
  LEVE: 'fill-info',
  MODERADA: 'fill-warning',
  GRAVE: 'fill-danger',
}

const CENTRO = ESCALA_DA_POSICAO / 2

/** Fração 0..1 → milésimo inteiro, preso à escala (clique na borda não vaza). */
function paraMilesimos(fracao: number): number {
  return Math.min(ESCALA_DA_POSICAO, Math.max(0, Math.round(fracao * ESCALA_DA_POSICAO)))
}

export interface DiagramaDoCarroProps {
  readonly avarias: readonly AvariaNoDiagrama[]
  /** Recebe a posição clicada em milésimos (0..1000) dos dois eixos. */
  readonly aoMarcar?: ((x: number, y: number) => void) | undefined
  readonly somenteLeitura?: boolean
  /** Nome do diagrama para leitor de tela ("Avarias da saída", …). */
  readonly rotulo?: string
  readonly className?: string
}

export function DiagramaDoCarro({
  avarias,
  aoMarcar,
  somenteLeitura = false,
  rotulo = 'Diagrama do carro',
  className,
}: DiagramaDoCarroProps): ReactElement {
  const interativo = !somenteLeitura && aoMarcar !== undefined

  function aoClicar(evento: MouseEvent<SVGSVGElement>): void {
    // `!interativo` já garante `aoMarcar` definido — o TypeScript estreita
    // através do alias, então nada de checar o callback de novo aqui.
    if (!interativo) {
      return
    }

    const retangulo = evento.currentTarget.getBoundingClientRect()

    if (retangulo.width === 0 || retangulo.height === 0) {
      return
    }

    aoMarcar(
      paraMilesimos((evento.clientX - retangulo.left) / retangulo.width),
      paraMilesimos((evento.clientY - retangulo.top) / retangulo.height),
    )
  }

  function aoTeclar(evento: KeyboardEvent<SVGSVGElement>): void {
    if (!interativo) {
      return
    }

    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault()
      aoMarcar(CENTRO, CENTRO)
    }
  }

  return (
    <svg
      viewBox={`0 0 ${String(ESCALA_DA_POSICAO)} ${String(ESCALA_DA_POSICAO)}`}
      className={cn(
        'h-auto w-full select-none rounded-lg border border-border bg-surface text-fg-muted',
        interativo &&
          'cursor-crosshair focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      role={interativo ? 'button' : 'img'}
      aria-label={interativo ? `${rotulo} — clique para marcar uma avaria` : rotulo}
      {...(interativo ? { tabIndex: 0, onClick: aoClicar, onKeyDown: aoTeclar } : {})}
    >
      {/* Carroceria — cápsula com a frente para cima. */}
      <path
        d="M500 60 C620 60 680 110 683 205 L691 770 C695 875 612 940 500 940 C388 940 305 875 309 770 L317 205 C320 110 380 60 500 60 Z"
        fill="currentColor"
        fillOpacity={0.05}
        stroke="currentColor"
        strokeWidth={8}
      />

      {/* Faróis e lanternas — os dois pares que dizem qual ponta é a frente. */}
      <rect x={360} y={78} width={90} height={18} rx={9} fill="currentColor" fillOpacity={0.25} />
      <rect x={550} y={78} width={90} height={18} rx={9} fill="currentColor" fillOpacity={0.25} />
      <rect x={362} y={906} width={86} height={14} rx={7} fill="currentColor" fillOpacity={0.25} />
      <rect x={552} y={906} width={86} height={14} rx={7} fill="currentColor" fillOpacity={0.25} />

      {/* Vinco do capô. */}
      <path
        d="M345 252 Q500 214 655 252"
        fill="none"
        stroke="currentColor"
        strokeWidth={5}
        strokeOpacity={0.7}
      />

      {/* Para-brisa (afunila do capô para o teto). */}
      <path
        d="M358 288 L642 288 L608 402 L392 402 Z"
        fill="currentColor"
        fillOpacity={0.12}
        stroke="currentColor"
        strokeWidth={5}
      />

      {/* Teto. */}
      <rect
        x={386}
        y={418}
        width={228}
        height={208}
        rx={26}
        fill="currentColor"
        fillOpacity={0.05}
        stroke="currentColor"
        strokeWidth={5}
      />

      {/* Vidro traseiro (alarga do teto para a traseira). */}
      <path
        d="M394 642 L606 642 L636 748 L364 748 Z"
        fill="currentColor"
        fillOpacity={0.12}
        stroke="currentColor"
        strokeWidth={5}
      />

      {/* Vidros laterais. */}
      <path
        d="M352 320 L374 410 L374 630 L358 720 Z"
        fill="currentColor"
        fillOpacity={0.12}
        stroke="currentColor"
        strokeWidth={4}
      />
      <path
        d="M648 320 L626 410 L626 630 L642 720 Z"
        fill="currentColor"
        fillOpacity={0.12}
        stroke="currentColor"
        strokeWidth={4}
      />

      {/* Retrovisores. */}
      <path
        d="M318 302 L260 326 L318 350 Z"
        fill="currentColor"
        fillOpacity={0.2}
        stroke="currentColor"
        strokeWidth={5}
      />
      <path
        d="M682 302 L740 326 L682 350 Z"
        fill="currentColor"
        fillOpacity={0.2}
        stroke="currentColor"
        strokeWidth={5}
      />

      {/* Marcadores — o número casa com a lista de avarias exibida ao lado. */}
      {avarias.map((avaria, indice) => {
        const numero = String(indice + 1)
        const detalhes =
          avaria.descricao === undefined || avaria.descricao.length === 0
            ? ''
            : ` — ${avaria.descricao}`

        return (
          <g
            key={`${numero}-${String(avaria.posicaoXEmMilesimos)}-${String(avaria.posicaoYEmMilesimos)}`}
          >
            <title>
              {`${numero}. ${ROTULO_DO_TIPO_DE_AVARIA[avaria.tipo]} (${ROTULO_DA_GRAVIDADE[avaria.gravidade]})${detalhes}`}
            </title>
            <circle
              cx={avaria.posicaoXEmMilesimos}
              cy={avaria.posicaoYEmMilesimos}
              r={26}
              className={PREENCHIMENTO_DA_GRAVIDADE[avaria.gravidade]}
              stroke="currentColor"
              strokeOpacity={0.35}
              strokeWidth={3}
            />
            <text
              x={avaria.posicaoXEmMilesimos}
              y={avaria.posicaoYEmMilesimos}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={32}
              fontWeight={600}
              className="fill-black"
            >
              {numero}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
